/**
 * ITSpendReport — Multi-dimension IT spend report with site-level breakdown,
 * top/bottom 10 widgets, branch ranking comparison, budget overlay, and CSV export.
 *
 * Requirements: 43.1-43.13
 */

import { useState, useEffect, useCallback } from 'react';
import {
  reportService,
  ITSpendReportParams,
  ITSpendReportResult,
  ITSpendSiteRow,
} from '@/services/reportService';

const SPEND_CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'circuits', label: 'Circuits' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'invoiced', label: 'Invoiced' },
] as const;



function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function ITSpendReport() {
  const [siteNumber, setSiteNumber] = useState('');
  const [division, setDivision] = useState('');
  const [region, setRegion] = useState('');
  const [spendCategory, setSpendCategory] = useState('');
  const [includeBudgetOverlay, setIncludeBudgetOverlay] = useState(false);

  const [result, setResult] = useState<ITSpendReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback((): ITSpendReportParams => ({
    siteNumber: siteNumber || undefined,
    division: division || undefined,
    region: region || undefined,
    spendCategory: (spendCategory as ITSpendReportParams['spendCategory']) || undefined,
    includeBudgetOverlay: includeBudgetOverlay || undefined,
  }), [siteNumber, division, region, spendCategory, includeBudgetOverlay]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.getITSpendReport(buildParams());
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleExport = () => {
    const url = reportService.getITSpendExportUrl(buildParams());
    window.open(url, '_blank');
  };

  return (
    <div >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-text-primary">IT Spend Report</h1>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-baker-red text-white rounded-lg font-medium hover:bg-baker-red/90 hover:bg-baker-red/90"
          aria-label="Export report to CSV"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label htmlFor="siteFilter" className="block text-sm font-medium mb-1">Site Number</label>
          <input
            id="siteFilter"
            type="text"
            value={siteNumber}
            onChange={e => setSiteNumber(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
            placeholder="e.g., 001"
          />
        </div>
        <div>
          <label htmlFor="divisionFilter" className="block text-sm font-medium mb-1">Division</label>
          <input
            id="divisionFilter"
            type="text"
            value={division}
            onChange={e => setDivision(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
            placeholder="Filter by division"
          />
        </div>
        <div>
          <label htmlFor="regionFilter" className="block text-sm font-medium mb-1">Region</label>
          <input
            id="regionFilter"
            type="text"
            value={region}
            onChange={e => setRegion(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
            placeholder="Filter by region"
          />
        </div>
        <div>
          <label htmlFor="spendCatFilter" className="block text-sm font-medium mb-1">Spend Category</label>
          <select
            id="spendCatFilter"
            value={spendCategory}
            onChange={e => setSpendCategory(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            {SPEND_CATEGORY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeBudgetOverlay}
              onChange={e => setIncludeBudgetOverlay(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm font-medium">Budget Overlay</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
          {error}
        </div>
      )}

      {loading && <div className="text-slate-400">Loading report...</div>}

      {result && !loading && (
        <>
          {/* Grand total summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <SummaryCard label="Contract Spend" value={formatCurrency(result.totalContractSpend)} />
            <SummaryCard label="Circuit Spend" value={formatCurrency(result.totalCircuitSpend)} />
            <SummaryCard label="Hardware Spend" value={formatCurrency(result.totalHardwareSpend)} />
            <SummaryCard label="Invoiced Spend" value={formatCurrency(result.totalInvoicedSpend)} />
            <SummaryCard label="Grand Total" value={formatCurrency(result.grandTotal)} highlight />
          </div>

          {/* Top 10 / Bottom 10 widgets */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <RankingWidget title="Top 10 Sites by IT Spend" sites={result.top10} variant="top" />
            <RankingWidget title="Bottom 10 Sites by IT Spend" sites={result.bottom10} variant="bottom" />
          </div>

          {/* Site-level detail table */}
          <h2 className="text-lg font-medium mb-3">Site-Level Spend Breakdown</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-page">
                  <th className="border px-3 py-2 text-left">Site</th>
                  <th className="border px-3 py-2 text-left">Branch Name</th>
                  <th className="border px-3 py-2 text-right">Contracts</th>
                  <th className="border px-3 py-2 text-right">Circuits</th>
                  <th className="border px-3 py-2 text-right">Hardware</th>
                  <th className="border px-3 py-2 text-right">Invoiced</th>
                  <th className="border px-3 py-2 text-right">Total</th>
                  <th className="border px-3 py-2 text-right">Employees</th>
                  <th className="border px-3 py-2 text-right">$/Employee</th>
                  {includeBudgetOverlay && (
                    <>
                      <th className="border px-3 py-2 text-right">Budgeted</th>
                      <th className="border px-3 py-2 text-right">Variance</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {result.sites.map((site: ITSpendSiteRow) => (
                  <tr key={site.siteNumber} className="hover:bg-page">
                    <td className="border px-3 py-2">
                      <a href={`/sites/${site.siteNumber}`} className="text-baker-red hover:underline">
                        {site.siteNumber}
                      </a>
                    </td>
                    <td className="border px-3 py-2">{site.branchName}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(site.contractSpend)}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(site.circuitSpend)}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(site.hardwareSpend)}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(site.invoicedSpend)}</td>
                    <td className="border px-3 py-2 text-right font-medium">{formatCurrency(site.totalSpend)}</td>
                    <td className="border px-3 py-2 text-right">{site.employeeCount ?? '—'}</td>
                    <td className="border px-3 py-2 text-right">
                      {site.spendPerEmployee !== undefined ? formatCurrency(site.spendPerEmployee) : '—'}
                    </td>
                    {includeBudgetOverlay && (
                      <>
                        <td className="border px-3 py-2 text-right">
                          {site.budgetedAmount !== undefined ? formatCurrency(site.budgetedAmount) : '—'}
                        </td>
                        <td className={`border px-3 py-2 text-right ${(site.dollarVariance ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {site.dollarVariance !== undefined ? formatCurrency(site.dollarVariance) : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {result.sites.length === 0 && (
                  <tr>
                    <td colSpan={20} className="border px-4 py-8 text-center text-slate-500">
                      No site data found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-white rounded-lg border border-border p-5 ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${highlight ? 'text-blue-700' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

function RankingWidget({
  title,
  sites,
  variant,
}: {
  title: string;
  sites: ITSpendSiteRow[];
  variant: 'top' | 'bottom';
}) {
  return (
    <div className="bg-white rounded-lg border border-border p-5 bg-white">
      <h3 className="text-sm font-medium text-slate-700 mb-3">{title}</h3>
      {sites.length === 0 ? (
        <div className="text-slate-400 text-sm">No data available.</div>
      ) : (
        <div className="space-y-2">
          {sites.map((site, idx) => (
            <div key={site.siteNumber} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  variant === 'top' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {idx + 1}
                </span>
                <a href={`/sites/${site.siteNumber}`} className="text-baker-red hover:underline">
                  {site.siteNumber}
                </a>
                <span className="text-slate-500">{site.branchName}</span>
              </div>
              <span className="font-medium">{formatCurrency(site.totalSpend)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
